import { z } from 'zod';
/**
 * The daily report — what it measures, who may read which part of it, and how
 * each department's version differs.
 *
 * One file, read by the API that computes the figures and by both portals that
 * render them, because the alternative is a section the employee can toggle
 * that the client's page has never heard of.
 */
/* ---------- departments ---------- */
/**
 * Who the report is from.
 *
 * A department, not a person. Two people in Sales send one Sales report
 * between them; the client should not receive two reports because two people
 * worked that day.
 *
 * GENERAL is the all-round report that predates the others — every report
 * written before departments existed is one of these, and it stays the default
 * so a client with no departmental split keeps exactly what they had.
 */
export const REPORT_TYPES = [
    'GENERAL',
    'SALES',
    'MARKETING',
    'BACK_OFFICE',
    'CREATIVE',
    'CUSTOMER_SUCCESS',
    'FINANCE',
];
export const REPORT_TYPE_LABELS = {
    GENERAL: 'General',
    SALES: 'Sales',
    MARKETING: 'Marketing',
    BACK_OFFICE: 'Back office',
    CREATIVE: 'Creative',
    CUSTOMER_SUCCESS: 'Customer success',
    FINANCE: 'Finance',
};
/** One line under the picker, so somebody choosing knows which is theirs. */
export const REPORT_TYPE_HINTS = {
    GENERAL: 'Everything, in one report. The right choice when one team does all of it.',
    SALES: 'Calls, follow-ups, the pipeline and what closed.',
    MARKETING: 'Posts published, reach, campaigns and the leads they brought in.',
    BACK_OFFICE: 'Records kept current, tasks cleared, paperwork moved.',
    CREATIVE: 'Design work delivered, posts produced, approvals waiting.',
    CUSTOMER_SUCCESS: 'Conversations had, issues resolved, meetings held.',
    FINANCE: 'Invoices raised, payments in, what is still outstanding.',
};
/* ---------- sections ---------- */
/**
 * The parts a report is assembled from.
 *
 * A section is the unit of three separate things, which is why it is one list:
 * what the template shows, what the author may hide, and what the client is
 * allowed to see. Splitting those apart is how a client ends up able to read a
 * section the author thought they had turned off.
 */
export const REPORT_SECTIONS = [
    'summary',
    'calls',
    'mails',
    'messages',
    'leads',
    'pipeline',
    'tasks',
    'appointments',
    'records',
    'content',
    'finance',
    'notes',
    'team',
    'plan',
];
export const SECTION_LABELS = {
    summary: 'Headline',
    calls: 'Calls',
    mails: 'Emails',
    messages: 'Messages',
    leads: 'Leads',
    pipeline: 'Pipeline',
    tasks: 'Tasks',
    appointments: 'Meetings',
    records: 'Record activity',
    content: 'Content published',
    finance: 'Invoices and payments',
    notes: 'Notes',
    team: 'Who did what',
    plan: 'Tomorrow',
};
export const SECTION_HINTS = {
    summary: 'The one-line verdict on the day.',
    calls: 'How many were made, how many connected, and for how long.',
    mails: 'Emails sent to this client’s contacts.',
    messages: 'WhatsApp, SMS and LinkedIn messages sent.',
    leads: 'New enquiries that arrived today.',
    pipeline: 'Deals that moved, and what closed.',
    tasks: 'Work raised and finished on this client’s board. Never internal tasks.',
    appointments: 'Meetings booked and held.',
    records: 'Every change made to a lead, contact or company today.',
    content: 'Posts published and creative delivered.',
    finance: 'Invoices raised and payments received.',
    notes: 'What the team wrote by hand.',
    team: 'The same figures, split by person.',
    plan: 'What happens tomorrow.',
};
/**
 * Which sections each department's report is built from, in order.
 *
 * This is the template. Choosing Marketing does not merely relabel the page —
 * it changes which figures are gathered and the order they are read in, so
 * somebody filling in a Creative report is not scrolling past a call table
 * that will always say zero.
 *
 * `summary` and `notes` are in every one of them on purpose: the headline is
 * the only part most clients read, and the notes are the only part no query
 * can produce.
 */
export const REPORT_TEMPLATES = {
    GENERAL: [
        'summary',
        'calls',
        'mails',
        'messages',
        'leads',
        'pipeline',
        'tasks',
        'appointments',
        'records',
        'notes',
        'team',
        'plan',
    ],
    SALES: [
        'summary',
        'calls',
        'leads',
        'pipeline',
        'appointments',
        'mails',
        'messages',
        'tasks',
        'notes',
        'team',
        'plan',
    ],
    MARKETING: ['summary', 'content', 'leads', 'messages', 'records', 'notes', 'plan'],
    BACK_OFFICE: ['summary', 'tasks', 'records', 'mails', 'appointments', 'notes', 'plan'],
    CREATIVE: ['summary', 'content', 'tasks', 'notes', 'plan'],
    CUSTOMER_SUCCESS: [
        'summary',
        'calls',
        'messages',
        'mails',
        'appointments',
        'tasks',
        'notes',
        'plan',
    ],
    FINANCE: ['summary', 'finance', 'tasks', 'mails', 'notes', 'plan'],
};
/**
 * What the client sees by default, per section.
 *
 * Deliberately not "everything". `records` is the raw edit log and `team` is a
 * per-person breakdown — both are how the work got done rather than what the
 * client got, and a client reading "Priya edited this lead 14 times" is being
 * shown the wrong thing. The author can turn either on for a client who wants
 * that level of detail.
 */
export const SECTION_CLIENT_DEFAULT = {
    summary: true,
    calls: true,
    mails: true,
    messages: true,
    leads: true,
    pipeline: true,
    tasks: true,
    appointments: true,
    records: false,
    content: true,
    finance: true,
    notes: true,
    team: false,
    plan: true,
};
/**
 * Resolve the visibility actually in force.
 *
 * Three inputs collapse to one answer: the template decides whether a section
 * exists at all, the stored choice wins where it was made, and the default
 * covers the rest. A section outside the template is always false — a Creative
 * report has no call figures, so "show calls" would promise a client a section
 * that renders empty.
 */
export function resolveVisibility(type, stored) {
    const inTemplate = new Set(REPORT_TEMPLATES[type]);
    const out = {};
    for (const section of REPORT_SECTIONS) {
        if (!inTemplate.has(section)) {
            out[section] = false;
            continue;
        }
        out[section] = stored?.[section] ?? SECTION_CLIENT_DEFAULT[section];
    }
    return out;
}
export const METRIC_LABELS = {
    callsMade: 'Calls made',
    callsConnected: 'Calls connected',
    callMinutes: 'Minutes on calls',
    mailsSent: 'Emails sent',
    messagesSent: 'Messages sent',
    whatsappSent: 'WhatsApp messages',
    leadsAdded: 'New leads',
    leadsWon: 'Deals won',
    leadsLost: 'Deals lost',
    contactsAdded: 'New contacts',
    companiesAdded: 'New companies',
    tasksCreated: 'Tasks raised',
    tasksCompleted: 'Tasks completed',
    appointmentsBooked: 'Meetings booked',
    meetingsLogged: 'Meetings held',
    notesAdded: 'Notes written',
    postsPublished: 'Posts published',
    invoicesRaised: 'Invoices raised',
    paymentsReceived: 'Payments received',
    paymentsPaise: 'Payments value',
    recordChanges: 'Record updates',
};
/** Which figures belong to which section, so a hidden section hides its numbers too. */
export const SECTION_METRICS = {
    calls: ['callsMade', 'callsConnected', 'callMinutes'],
    mails: ['mailsSent'],
    messages: ['messagesSent', 'whatsappSent'],
    leads: ['leadsAdded', 'contactsAdded', 'companiesAdded'],
    pipeline: ['leadsWon', 'leadsLost'],
    tasks: ['tasksCreated', 'tasksCompleted'],
    appointments: ['appointmentsBooked', 'meetingsLogged'],
    records: ['recordChanges'],
    content: ['postsPublished'],
    finance: ['invoicesRaised', 'paymentsReceived', 'paymentsPaise'],
    notes: ['notesAdded'],
};
/* ---------- input ---------- */
const dateKey = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD.');
export const reportTypeSchema = z.enum(REPORT_TYPES);
export const reportOpenSchema = z.object({
    date: dateKey.optional(),
    type: reportTypeSchema.default('GENERAL'),
});
export const reportHistoryQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
    type: reportTypeSchema.optional(),
    status: z.enum(['DRAFT', 'SENT', 'FAILED']).optional(),
    from: dateKey.optional(),
    to: dateKey.optional(),
});
/**
 * Editing a draft.
 *
 * Every field optional and applied only when present, so the section-visibility
 * switches and the notes box can save independently without one wiping the
 * other.
 */
export const reportUpdateSchema = z.object({
    highlight: z.string().trim().max(600).nullish(),
    notes: z.string().trim().max(8000).nullish(),
    tomorrowPlan: z.string().trim().max(2000).nullish(),
    visibleToClient: z.boolean().optional(),
    sections: z.record(z.enum(REPORT_SECTIONS), z.boolean()).optional(),
    /**
     * Typed-over figures. Null clears one and restores the computed value —
     * which is why this is nullable rather than merely optional.
     */
    overrides: z.record(z.string(), z.number().int().min(0).max(1_000_000).nullable()).optional(),
});
//# sourceMappingURL=report.schema.js.map